// Layer: application. Domain only.
//
// Client-side validation mirrors storia.domain.playbook invariants. The server is the
// authoritative validator; this exists so an operator authoring a playbook on a tablet
// sees the problem as they type rather than on save.
import { ALLOWED_ACTION_KINDS, type Playbook, type PlaybookNode } from "../domain/types";

export class PlaybookValidationError extends Error {}

export function validatePlaybook(pb: Playbook): void {
  if (!pb.name.trim()) throw new PlaybookValidationError("name required");
  if (pb.version < 1) throw new PlaybookValidationError("version must be >= 1");
  if (pb.nodes.length === 0) throw new PlaybookValidationError("playbook is empty");

  const keys = pb.nodes.map((n) => n.key);
  if (keys.some((k) => !k.trim())) {
    throw new PlaybookValidationError("node keys must be non-empty");
  }
  if (new Set(keys).size !== keys.length) {
    throw new PlaybookValidationError("node keys must be unique");
  }
  const keySet = new Set(keys);
  const index = new Map<string, PlaybookNode>();
  for (const n of pb.nodes) index.set(n.key, n);

  for (const n of pb.nodes) {
    for (const next of n.next) {
      if (!keySet.has(next)) {
        throw new PlaybookValidationError(`node ${n.key} references unknown ${next}`);
      }
      if (next === n.key) {
        throw new PlaybookValidationError(`node ${n.key} references itself`);
      }
    }
  }

  // Iterative 3-colour DFS. Iterative because an operator-authored graph can be long
  // and a stack overflow is a worse error message than a cycle report.
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const colour = new Map<string, 0 | 1 | 2>(keys.map((k) => [k, WHITE]));
  for (const root of keys) {
    if (colour.get(root) !== WHITE) continue;
    const stack: Array<[string, boolean]> = [[root, false]];
    while (stack.length) {
      const [key, exiting] = stack.pop()!;
      if (exiting) {
        colour.set(key, BLACK);
        continue;
      }
      if (colour.get(key) === GREY) {
        throw new PlaybookValidationError(`cycle at ${key}`);
      }
      if (colour.get(key) === BLACK) continue;
      colour.set(key, GREY);
      stack.push([key, true]);
      for (const next of index.get(key)!.next) {
        if (colour.get(next) !== BLACK) stack.push([next, false]);
      }
    }
  }

  const triggers = pb.nodes.filter((n) => n.kind === "trigger");
  const actions = pb.nodes.filter((n) => n.kind === "action");
  if (triggers.length === 0) {
    throw new PlaybookValidationError("at least one trigger required");
  }
  if (actions.length === 0) {
    throw new PlaybookValidationError("at least one action required");
  }
  if (actions.some((a) => a.next.length > 0)) {
    throw new PlaybookValidationError("action nodes are terminal");
  }

  for (const node of pb.nodes) {
    if (node.kind === "action") {
      if (!(ALLOWED_ACTION_KINDS as readonly string[]).includes(node.spec.kind)) {
        throw new PlaybookValidationError(`action kind ${node.spec.kind} is not permitted`);
      }
      if (node.spec.auto_approve && !node.spec.reversible) {
        throw new PlaybookValidationError(
          `${node.key}: non-reversible actions must require human approval`,
        );
      }
    }
    if (node.kind === "guardrail") {
      if (!node.spec.predicate.trim()) {
        throw new PlaybookValidationError(`guardrail ${node.key} has no predicate`);
      }
      if (node.next.length === 0) {
        throw new PlaybookValidationError(`guardrail ${node.key} gates nothing`);
      }
    }
  }

  const reachable = new Set<string>();
  const stack = triggers.map((t) => t.key);
  while (stack.length) {
    const k = stack.pop()!;
    if (reachable.has(k)) continue;
    reachable.add(k);
    stack.push(...index.get(k)!.next);
  }
  const unreachable = keys.filter((k) => !reachable.has(k));
  if (unreachable.length > 0) {
    throw new PlaybookValidationError(`unreachable nodes: ${unreachable.join(", ")}`);
  }
}

/** Validate without throwing — for live feedback while typing. */
export function playbookError(pb: Playbook): string | null {
  try {
    validatePlaybook(pb);
    return null;
  } catch (e) {
    return e instanceof PlaybookValidationError ? e.message : String(e);
  }
}
